import nacl from 'tweetnacl';
import { generateMnemonic, mnemonicToEntropy } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { sha256 } from 'js-sha256';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 as sha256Hkdf } from '@noble/hashes/sha256';
import { Keypair } from '@solana/web3.js';
import { kyber } from 'kyber-crystals';

/**
 * Generate a new Unlink keypair with mnemonic for recovery.
 */
export async function generateUnlinkKeypair(
  mnemonic?: string
): Promise<{
  mnemonic: string;
  basePrivateKey: Uint8Array;
  basePublicKey: Uint8Array;
  viewPrivateKey: Uint8Array;
  viewPublicKey: Uint8Array;
  kyberPublicKey: Uint8Array;
  kyberPrivateKey: Uint8Array;
  fundingPrivateKey: Uint8Array;
  fundingPublicKey: Uint8Array;
}> {
  if (mnemonic && mnemonic.trim().split(/\s+/).length !== 24) {
    throw new Error('Invalid mnemonic: must be 24 words (256-bit entropy)');
  }

  // Use or generate mnemonic
  const finalMnemonic = mnemonic ?? generateMnemonic(wordlist, 256);
  const entropyBytes = mnemonicToEntropy(finalMnemonic, wordlist);

  const encoder = new TextEncoder();

  // === Base keypair ===
  const basePrivateKey = hkdf(sha256Hkdf, entropyBytes, undefined, encoder.encode('base'), 32);
  const baseKeypair = nacl.box.keyPair.fromSecretKey(basePrivateKey);

  // === View keypair ===
  const viewPrivateKey = hkdf(sha256Hkdf, entropyBytes, undefined, encoder.encode('view'), 32);
  const viewKeypair = nacl.box.keyPair.fromSecretKey(viewPrivateKey);

  // === Kyber keypair (never deterministic) ===
  const { 
    privateKey: kyberPrivateKey,
    publicKey: kyberPublicKey, 
  } = await kyber.keyPair();

  // === Funding keypair ===
  const fundingSeed = hkdf(sha256Hkdf, entropyBytes, undefined, encoder.encode('funding'), 32);
  const fundingKeypair = Keypair.fromSeed(fundingSeed);

  return {
    mnemonic: finalMnemonic,
    basePrivateKey,
    basePublicKey: baseKeypair.publicKey,
    viewPrivateKey,
    viewPublicKey: viewKeypair.publicKey,
    kyberPrivateKey,
    kyberPublicKey,
    fundingPrivateKey: fundingSeed,
    fundingPublicKey: fundingKeypair.publicKey.toBytes(),
  };
}